import React, { useEffect, useState } from "react";
import { FaCalendarAlt, FaMapMarkerAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EmailModal from "./EmailModal";
import PaymentResultModal from "./PaymentResultModal";

interface ProgramItem {
  id: number;
  title: string;
  category: string;
  date: string;
  start_time: string;
  location: string;
  play_id?: number;
  event_id?: number;
  price_cents?: number;
  has_tickets?: boolean;
  tickets_available?: boolean;
  ticket_status_message_key?: string;
  max_tickets_per_order?: number;
}

const Program = () => {
  const [programs, setPrograms] = useState<ProgramItem[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentResult, setPaymentResult] = useState({ isSuccess: false, message: "" });
  const [selectedProgram, setSelectedProgram] = useState<{ id: number; title: string; maxTickets?: number } | null>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetch(`/api/programs?lang=${i18n.language}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setPrograms(data);
        } else {
          console.error("Programs API response is not an array:", data);
          setPrograms([]);
        }
      })
      .catch((err) => console.error("Programs fetch error:", err));
  }, [i18n.language]);

  // Handle URL query parameters for month
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const monthParam = urlParams.get('month');

      if (monthParam) {
        const parsedDate = new Date(monthParam + '-01'); // Add day to make it valid
        const currentYear = new Date().getFullYear();
        
        // Validate date: must be valid, between 2020 and current year + 1
        if (!isNaN(parsedDate.getTime()) && 
            parsedDate.getFullYear() >= 2020 && 
            parsedDate.getFullYear() <= currentYear + 1) {
          setCurrentMonth(parsedDate);
        } else {
          console.warn('Invalid month parameter, using current date:', monthParam);
          setCurrentMonth(new Date());
          // Clean up invalid URL parameter
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('month');
          window.history.replaceState({}, '', newUrl.toString());
        }
      }
    } catch (error) {
      console.warn('Error parsing URL month parameter:', error);
      setCurrentMonth(new Date());
    }
  }, []);

  const getCurrentMonthPrograms = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // Validate currentMonth
    if (isNaN(year) || isNaN(month)) {
      return [];
    }

    return programs.filter(program => {
      try {
        const programDate = new Date(program.date);
        // Check if date is valid
        if (isNaN(programDate.getTime())) {
          console.warn('Invalid program date:', program.date);
          return false;
        }
        return programDate.getFullYear() === year && programDate.getMonth() === month;
      } catch (error) {
        console.warn('Error parsing program date:', program.date, error);
        return false;
      }
    });
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => {
      try {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() - 1);
        // Validate the new date
        if (isNaN(newDate.getTime())) {
          return new Date(); // fallback to current date
        }
        return newDate;
      } catch (error) {
        console.warn('Error in handlePreviousMonth:', error);
        return new Date();
      }
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      try {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() + 1);
        // Validate the new date
        if (isNaN(newDate.getTime())) {
          return new Date(); // fallback to current date
        }
        return newDate;
      } catch (error) {
        console.warn('Error in handleNextMonth:', error);
        return new Date();
      }
    });
  };

  const handleCheckoutClick = (programId: number, programTitle: string, maxTickets?: number) => {
    setSelectedProgram({ id: programId, title: programTitle, maxTickets });
    setIsEmailModalOpen(true);
  };

  const handleEmailSubmit = async (email: string, quantity: number) => {
    setIsEmailModalOpen(false);

    if (!selectedProgram) return;

    try {
      const res = await fetch(`/api/programs/${selectedProgram.id}/start_checkout?lang=${i18n.language}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, quantity })
      });
      const data = await res.json();

      if (data.url) {
        // Ücretli bilet - Stripe checkout'a yönlendir
        window.location.href = data.url;
      } else if (data.free_ticket && data.success) {
        // Ücretsiz bilet - başarı mesajı göster
        setPaymentResult({
          isSuccess: true,
          message: data.message || "Ücretsiz biletiniz kaydedildi. Onay e-postası gönderildi."
        });
        setIsPaymentModalOpen(true);
      } else {
        // Hata durumu
        setPaymentResult({
          isSuccess: false,
          message: "Ödeme başlatılamadı: " + (data.error || "Bilinmeyen hata")
        });
        setIsPaymentModalOpen(true);
      }
    } catch (error) {
      setPaymentResult({
        isSuccess: false,
        message: "Ödeme başlatılamadı: " + error
      });
      setIsPaymentModalOpen(true);
    }
  };

  const currentMonthPrograms = getCurrentMonthPrograms();

  let currentMonthName;
  try {
    if (isNaN(currentMonth.getTime())) {
      currentMonthName = new Date().toLocaleString(i18n.language, { month: 'long', year: 'numeric' });
    } else {
      currentMonthName = currentMonth.toLocaleString(i18n.language, { month: 'long', year: 'numeric' });
    }
  } catch (error) {
    console.warn('Error formatting current month name:', error);
    currentMonthName = new Date().toLocaleString(i18n.language, { month: 'long', year: 'numeric' });
  }

  return (
    <>
      <section
        id="program-section"
        className="w-full flex flex-col items-center px-4 py-1 mt-4 bg-black text-white font-sans"
      >
        {/* Program ana başlığı */}
        <h2 className="text-3xl font-extrabold underline underline-offset-8 decoration-brand-red-text mb-8 text-center">
          {t("program.title")}
        </h2>

        {/* Header with navigation */}
        <div className="flex items-center justify-center gap-6 mb-10 w-full max-w-4xl">
          <button
            onClick={handlePreviousMonth}
            className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white hover:text-brand-red-text transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer"
            aria-label={t("program.previousMonth")}
          >
            <FaChevronLeft className="text-lg" />
          </button>

          <h2 className="text-2xl md:text-3xl font-extrabold text-center">
            {currentMonthName}
          </h2>

          <button
            onClick={handleNextMonth}
            className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white hover:text-brand-red-text transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer"
            aria-label={t("program.nextMonth")}
          >
            <FaChevronRight className="text-lg" />
          </button>
        </div>

        {/* Empty state */}
        {currentMonthPrograms.length === 0 && (
          <div className="max-w-2xl w-full mx-auto">
            <div className="border border-red-500 rounded-xl p-8 text-center">
              <p className="text-lg text-gray-300">
                {t("program.noEvents")}
              </p>
            </div>
          </div>
        )}

        {/* Programs list */}
        {currentMonthPrograms.map((program) => {
          let day, month, dateObj;
          try {
            dateObj = new Date(program.date);
            if (isNaN(dateObj.getTime())) {
              // Invalid date fallback
              day = "?";
              month = "?";
            } else {
              day = dateObj.getDate();
              month = dateObj.toLocaleString(i18n.language, {
                month: "short",
              });
            }
          } catch (error) {
            console.warn('Error rendering program date:', program.date, error);
            day = "?";
            month = "?";
            dateObj = new Date(); // fallback for any other dateObj usage
          }

          return (
            <div
              key={program.id}
              className="flex bg-white text-black rounded-xl relative shadow-xl max-w-sm md:max-w-2xl w-full overflow-hidden min-h-[220px] mb-10"
            >
              {/* date */}
              <div className="text-center px-8 py-6 flex flex-col justify-center items-center min-w-[130px]">
                <span className="text-4xl md:text-5xl font-bold text-[#D8001D]">{day}</span>
                <span className="uppercase text-base md:text-lg text-gray-600 mt-2">
                  {month}
                </span>
              </div>

              {/* dashed line */}
              <div className="absolute top-0 bottom-0 left-[136px] w-[2px] border-l-4 border-dotted border-gray-400 z-0"></div>

              {/* circle */}
              <div className="absolute top-[-12px] left-[126px] w-6 h-6 bg-black rounded-full z-10"></div>
              <div className="absolute bottom-[-12px] left-[126px] w-6 h-6 bg-black rounded-full z-10"></div>

              {/* content */}
              <div className="flex-1 px-10 py-6 relative flex flex-col justify-between">
                <div>
                  <p className="text-base md:text-lg text-gray-500 font-semibold">
                    {program.category}
                  </p>
                  <h3 className="text-xl md:text-2xl font-bold mt-1 mb-4 text-black hover:text-[#D8001D] transition-colors">
                    {program.play_id ? (
                      <Link to={`/plays/${program.play_id}`}>{program.title}</Link>
                    ) : program.event_id ? (
                      <Link to={`/events/${program.event_id}`}>{program.title}</Link>
                    ) : (
                      program.title
                    )}
                  </h3>

                  <div className="flex items-center text-sm md:text-base text-gray-700 mb-2">
                    <FaCalendarAlt className="text-[#D8001D] mr-3" />
                    {dateObj.toLocaleDateString(i18n.language)} &nbsp;
                    {program.start_time}
                  </div>

                  <div className="flex items-center text-sm md:text-base text-gray-700">
                    <FaMapMarkerAlt className="text-[#D8001D] mr-3" />
                    {program.location}
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  {program.tickets_available ? (
                    <button 
                      onClick={() => handleCheckoutClick(program.id, program.title, program.max_tickets_per_order)}
                      className="bg-[#D8001D] hover:bg-[#B8001A] text-white px-4 py-4 rounded-md text-sm font-semibold transition-colors duration-200 cursor-pointer"
                    >
                      {t("program.ctaButton")}
                    </button>
                  ) : program.ticket_status_message_key && (
                    <div className="text-sm text-[#D8001D] italic font-medium">
                      {t(program.ticket_status_message_key)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Email Modal */}
      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSubmit={handleEmailSubmit}
        title={t("emailModal.title")}
        maxTickets={selectedProgram?.maxTickets ? Math.min(selectedProgram.maxTickets, 5) : 5}
      />

      {/* Payment Result Modal */}
      <PaymentResultModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        isSuccess={paymentResult.isSuccess}
        message={paymentResult.message}
      />
    </>
  );
};

export default Program;